import { Dispatch, SetStateAction } from "react";

import { fromJson, toJson } from "../utils/exportUtils";
import { FunctionalMap } from "../utils/objectUtils";

import { MoveActions } from "./moveActions";

export interface DataActions {
	newElement: (element: ElementDefinitionType) => void;
	deleteElement: (elementId: string) => void;
	addElementToContainer: (elementId: string, containerId: string) => void;
	deleteElementFromContainer: (elementId: string, containerId: string) => void;
	newContainer: (container: ContainerType) => void;
	deleteContainer: (containerId: string) => void;
	importData: (data: string) => void;
	exportData: () => string;
}

export function dataActionsHelper(
	setDisplayedElements: Dispatch<SetStateAction<Array<string>>>,
	setElementEditing: Dispatch<SetStateAction<ContainerElementRef | undefined>>,
	setContainerEditingId: Dispatch<SetStateAction<string | undefined>>,
	moveActions: MoveActions,
	elementsMap: FunctionalMap<string, ElementDefinitionType>,
	containersMap: FunctionalMap<string, ContainerType>,
	elementsPositionMap: FunctionalMap<string, ContainerElementRef>
): DataActions {
	return {
		newElement: element => {
			if (!element.id) {
				// TODO should also validate the other props, using element.validate() ?
				// Or the constructor initializes with default values...
				// Invalid element properties
				return;
			}

			if (elementsMap.has(element.id)) {
				// An element with this name already exists... Do nothing...
				return;
			}
			elementsMap.set(element.id, element);
			setDisplayedElements(displayedElements => [...displayedElements, element.id]);
		},
		deleteElement: elementId => {
			elementsMap.delete(elementId);
			elementsPositionMap.delete(elementId);
			setDisplayedElements(displayedElements =>
				displayedElements.filter(eltId => eltId !== elementId)
			);
			setElementEditing(elementEditing =>
				elementEditing?.elementId === elementId ? undefined : elementEditing
			);
		},
		addElementToContainer: (elementId, containerId) => {
			const container = containersMap.get(containerId);
			if (!container) {
				// Container unknown, do nothing
				return;
			}
			const element = elementsMap.get(elementId);
			if (!element) {
				// Element unknown, do nothing
				return;
			}
			if (elementsPositionMap.has(elementId)) {
				// Element already in a container, do nothing
				return;
			}

			const newContainer = { ...container, content: [...container.content] };
			const newRef = { elementId, containerId, position: {} };
			newContainer.content.push(elementId);

			containersMap.set(newContainer.id, newContainer);
			elementsPositionMap.set(newRef.elementId, newRef);

			setElementEditing(newRef);

			moveActions.moveElement("y", 0);

			// Force re-render of the list
			setDisplayedElements(list => [...list]);
		},
		deleteElementFromContainer: (elementId, containerId) => {
			const container = containersMap.get(containerId);
			if (!container) {
				// Container unknown, do nothing
				return;
			}

			// TODO update other content position for collisions if auto move down
			const newContainer = {
				...container,
				content: container.content.filter(eltId => eltId !== elementId)
			};

			containersMap.set(newContainer.id, newContainer);

			elementsPositionMap.delete(elementId);

			// Force re-render of the list
			setDisplayedElements(list => [...list]);
		},
		newContainer: container => {
			if (!container.id || !container.width || !container.height || !container.depth) {
				// Invalid container properties
				return;
			}
			if (containersMap.get(container.id)) {
				// A container with this name already exists... Do nothing...
				return;
			}
			containersMap.set(container.id, container);
			setContainerEditingId(container.id);
		},
		deleteContainer: containerId => {
			const container = containersMap.get(containerId);
			if (!container) {
				// Container unknown, do nothing...
				return;
			}

			container.content.forEach(eltId => {
				elementsPositionMap.delete(eltId);
			});

			containersMap.delete(containerId);

			setContainerEditingId(id => (id === containerId ? undefined : id));
			setElementEditing(elementEditing =>
				elementEditing?.containerId === containerId ? undefined : elementEditing
			);
		},
		importData: dataJson => {
			const data = fromJson(dataJson);
			elementsMap.clear();
			data.elements.forEach(element => {
				elementsMap.set(element.id, element);
			});
			containersMap.clear();
			data.containers.forEach(container => {
				containersMap.set(container.id, container);
			});
			elementsPositionMap.clear();
			data.elementsInContainers.forEach(ref => {
				elementsPositionMap.set(ref.elementId, ref);
			});
			setDisplayedElements(
				Array.from(elementsMap.values())
					.filter(({ id }) => !elementsPositionMap.has(id))
					.map(({ id }) => id)
			);
		},
		exportData: () =>
			toJson({
				elements: elementsMap,
				containers: containersMap,
				elementsInContainers: elementsPositionMap
			})
	};
}
